import update from 'immutability-helper';
import get from 'lodash/get';
import { orderBy } from 'natural-orderby';
import * as ACTIONS from './actions';
import { CACHE_ACTIONS } from '~/constants/cache-actions.constants';
import { LocalCacheHandler } from '~/utils/local-cache-handler';
import moment from 'moment';
import { COLUMN_IDS, COLUMN_NAMES, COLUMN_STYLES, generateColumns } from './constants';

const orderASC = (leads) => {
    const ordered = orderBy(leads, [(l) => {
        const task = get(l, 'additional_data.nextPendingTask');
        return task && task.due_at ? moment(task.due_at).valueOf() : null; 
    }], ['asc']);

    return ordered;
};

const parseAdditionalData = (lead) => {
    if (!lead) return lead;
    return {
        ...lead,
        additional_data: typeof lead.additional_data === 'string' 
            ? JSON.parse(lead.additional_data)
            : lead.additional_data
    };
};

const createColumn = (id) => ({
    id,
    name: COLUMN_NAMES[id],
    style: COLUMN_STYLES[id],
    data: [],
    hidden: false
});

// Inline loadingReducer
const isLoading = (state = true, action) => {
    switch (action.type) {
        case ACTIONS.LOAD.SUCCESS:
            return false;
        case ACTIONS.LOAD.INIT:
            return true;
        default:
            return state;
    }
};

// Inline selectedMonth reducer
const selectedMonth = (state, action) => {
    // Default state for selectedMonth (now using daily format as default)
    const defaultState = moment().format("YYYY-MM-DD");

    // Ensure we have a valid state
    const currentState = state !== undefined ? state : defaultState;
    
    try {
        switch (action.type) {
            case ACTIONS.SET_SELECTED_MONTH:
                return action.payload.month;
            default:
                return currentState;
        }
    } catch (error) {
        console.error('Error in selectedMonth reducer:', error);
        return defaultState;
    }
};

// Inline drawerReducer
const drawer = (state = { data: { id: null }, show: false, saving: false }, action) => {
    // Ensure we always have a valid state
    const currentState = state || { data: { id: null }, show: false, saving: false };
    
    switch (action.type) {
        case ACTIONS.SHOW:
            return update(currentState, {
                data: { $set: get(action, 'payload.data') ? get(action, 'payload.data') : { id: null } },
                show: { $set: true },
            });

        case ACTIONS.CLOSE:
            return update(currentState, {
                data: { $set: { id: null } },
                show: { $set: false },
                saving: { $set: false },
            });

        case ACTIONS.CREATE.INIT:
        case ACTIONS.SAVE.INIT:
            return update(currentState, {
                saving: { $set: true },
            });

        case ACTIONS.BATCH.SUCCESS:
        case ACTIONS.SAVE.SUCCESS:
        case ACTIONS.SAVE.FAIL:
        case ACTIONS.CREATE.SUCCESS:
        case ACTIONS.CREATE.FAIL:
        case ACTIONS.DELETE.SUCCESS:
        case ACTIONS.DELETE.FAIL:
            return update(currentState, {
                data: { $set: { id: null } },
                show: { $set: false },
                saving: { $set: false },
            });

        default:
            return currentState;
    }
};

// Helper function to map lead to appropriate column based on cadence progress
const mapLeadToColumn = (lead, columns) => {
  // Parse column_id as integer for consistent comparison
  var leadColumnId = parseInt(lead.column_id);

  // First, check if the lead has an explicit column_id set to Positivo or Negativo
  if (leadColumnId === COLUMN_IDS.POSITIVE) return columns.find(col => col.id === COLUMN_IDS.POSITIVE);
  if (leadColumnId === COLUMN_IDS.NEGATIVE) return columns.find(col => col.id === COLUMN_IDS.NEGATIVE);

  // For Positivo and Negativo columns, only show leads that have their column_id explicitly set
  // This prevents leads from being automatically placed in these columns based on cadence status
  const positivoColumn = columns.find(col => col.id === COLUMN_IDS.POSITIVE);
  const negativoColumn = columns.find(col => col.id === COLUMN_IDS.NEGATIVE);
  
  if (!lead.additional_data || !lead.additional_data.cadences) {
    // If no cadence data, put in Inicio column
    return columns.find(col => col.id === COLUMN_IDS.INICIO);
  }

  // Check cadence progress to determine which step column
  const cadences = lead.additional_data.cadences;
  for (const cadence of cadences) {
    if (cadence.data && Array.isArray(cadence.data)) {
      // Check if any tasks are done
      const hasCompletedTasks = cadence.data.some(task => task.status === 'done');
      
      if (!hasCompletedTasks) {
        // If no tasks are done yet, put in Leads column
        return columns.find(col => col.id === COLUMN_IDS.LEADS);
      }
      
      // Find the latest completed task to determine the current step column
      const completedTasks = cadence.data.filter(task => task.status === 'done');
      if (completedTasks.length > 0) {
        // Get the latest completed task (last in the array)
        const latestCompletedTask = completedTasks[completedTasks.length - 1];
        const stepColumn = columns.find(col => col.id === `step_${latestCompletedTask.uid}`);
        if (stepColumn) {
          return stepColumn;
        }
      }
      
      // If no tasks are completed yet, put in Leads column
      return columns.find(col => col.id === COLUMN_IDS.LEADS);
      
      // Note: Canceled tasks no longer automatically move leads to Negativo column
      // Leads must be explicitly moved to Negativo column via the drawer
    }
  }

  // If has cadence data but no steps are done, put in Leads column
  return columns.find(col => col.id === COLUMN_IDS.LEADS);
};

// Inline columnsReducer
const columns = (state = [], action) => {
  let currentState = state || [];

  switch (action.type) {
    case 'UPDATE_COLUMNS_FOR_CADENCE': {
      const { cadenceData } = action.payload;
      const newColumns = generateColumns(cadenceData);
      
      // Preserve existing lead data by mapping it to the new column structure
      const existingLeads = currentState.reduce((acc, column) => {
        if (column.data && column.data.length > 0) {
          acc.push(...column.data);
        }
        return acc;
      }, []);
      
      // Map existing leads to new columns
      const updatedColumns = newColumns.map((newColumn) => {
        const columnLeads = existingLeads.filter(lead => {
          const targetColumn = mapLeadToColumn(lead, newColumns);
          return targetColumn && targetColumn.id === newColumn.id;
        });
        return {
          ...newColumn,
          data: columnLeads,
          total: columnLeads.length,
        };
      });
      return updatedColumns;
    }
    case ACTIONS.LOAD.SUCCESS: {
      const handledState = action.payload.data.data.map(parseAdditionalData);
      
      // If we have dynamic columns, map leads to appropriate columns
      if (currentState.some(col => col.type === 'step')) {
        return currentState.map((column) => {
          // Get leads from server that should be in this column
          const serverLeadsForColumn = handledState.filter(lead => {
            const targetColumn = mapLeadToColumn(lead, currentState);
            return targetColumn && targetColumn.id === column.id;
          });
          return {
            ...column,
            data: serverLeadsForColumn,
            total: serverLeadsForColumn.length,
          };
        });
      }
      // Legacy behavior
      return currentState.map((column) => {
        // Parse column_id as integer for consistent comparison
        const currentColumn = handledState.filter(function(c) {
          return parseInt(c.column_id) === column.id;
        });
        return {
          ...column,
          data: currentColumn,
          total: currentColumn.length,
        };
      });
    }
    case ACTIONS.CREATE.SUCCESS: {
      const createdLead = get(action, 'payload.data.data');
      const parsedLead = parseAdditionalData(createdLead);
      
      // If we have dynamic columns, map the created lead to appropriate column
      if (currentState.some(col => col.type === 'step')) {
        const targetColumn = mapLeadToColumn(parsedLead, currentState);
        
        return currentState.map((column) => {
          const isTargetColumn = targetColumn && targetColumn.id === column.id;
          
          if (isTargetColumn) {
            // Lead should be in this column, add it
            return {
              ...column,
              data: [parsedLead, ...column.data],
              total: column.data.length + 1
            };
          }
          return column;
        });
      }
      
      // Legacy behavior for backward compatibility
      // Ensure column_id is parsed as integer for comparison
      var createdLeadColumnId = parseInt(createdLead.column_id);
      return currentState.map((column) => {
        if(column.id !== createdLeadColumnId) return column;
        const newData = [parsedLead, ...column.data];
        return {
          ...column,
          data: newData,
          total: newData.length
        };
      });
    }
    case ACTIONS.BATCH.SUCCESS: {
      const batchData = get(action, 'payload.data.data');
      const createdLeads = batchData && batchData.created ? batchData.created : (Array.isArray(batchData) ? batchData : []);
      return currentState.map((column) => {
        // Parse column_id as integer for consistent comparison
        const columnLeads = createdLeads.filter(function(l) {
          return parseInt(l.column_id) === column.id;
        });
        const newData = [...column.data, ...columnLeads.map(parseAdditionalData)];
        return {
          ...column,
          data: newData,
          total: newData.length
        };
      });
    }
    case ACTIONS.SAVE.INIT: {
      const lead = get(action, 'payload.request.data');
      // Parse column_id as integer for consistent comparison
      var leadColumnIdInit = parseInt(lead.column_id);
      return currentState.map((column) => {
        if(column.id !== leadColumnIdInit) return column;
        return {
          ...column,
          data: column.data.map(l => l.id === lead.id ? {...l, ...lead, isLoading: true} : l)
        };
      });
    }
    case ACTIONS.SAVE.SUCCESS: {
      const updatedLead = get(action, 'payload.data.data');
      const parsedLead = parseAdditionalData(updatedLead);
      
      // If we have dynamic columns, map the updated lead to appropriate column
      if (currentState.some(col => col.type === 'step')) {
        const targetColumn = mapLeadToColumn(parsedLead, currentState);
        
        return currentState.map((column) => {
          const hasLead = column.data.some(l => l.id === updatedLead.id);
          const isTargetColumn = targetColumn && targetColumn.id === column.id;
          
          if (hasLead && isTargetColumn) {
            // Lead is already in the correct column, just update it
            return {
              ...column,
              data: column.data.map(l => l.id === updatedLead.id ? parsedLead : l),
            };
          } else if (hasLead && !isTargetColumn) {
            // Lead is in wrong column, remove it
            return {
              ...column,
              data: column.data.filter(l => l.id !== updatedLead.id),
            };
          } else if (!hasLead && isTargetColumn) {
            // Lead should be in this column, add it
            return {
              ...column,
              data: [parsedLead, ...column.data],
            };
          }
          return column;
        });
      }
      
      // Legacy behavior for backward compatibility
      // Ensure column_id is parsed as integer for comparison
      var updatedLeadColumnId = parseInt(updatedLead.column_id);
      return currentState.map((column) => {
        if (column.id === updatedLeadColumnId) {
          return {
            ...column,
            data: column.data.map(l => l.id === updatedLead.id ? parsedLead : l),
          };
        } else if (column.data.some(l => l.id === updatedLead.id)) {
          return {
            ...column,
            data: column.data.filter(l => l.id !== updatedLead.id),
          };
        }
        return column;
      });
    }
    case ACTIONS.DELETE.SUCCESS: {
      const lead = get(action, 'meta.previousAction.payload.lead');
      // Parse column_id as integer for consistent comparison
      var deleteLeadColumnId = parseInt(lead.column_id);
      return currentState.map((column) => {
        if (column.id === deleteLeadColumnId) {
          return {
            ...column,
            data: column.data.filter(l => l.id !== lead.id),
          };
        }
        return column;
      });
    }
    case ACTIONS.MOVE.INIT: {
      const [sourceClone, destClone, droppableSource, droppableDestination] = action.props;
      const [removed] = sourceClone.splice(droppableSource.index, 1);

      // Set column_updated_at optimistically when moving
      var movedLead = Object.assign({}, removed, {
        column_updated_at: moment().toISOString(),
        column_id: parseInt(droppableDestination.droppableId)
      });
      destClone.splice(droppableDestination.index, 0, movedLead);

      return currentState.map((column) => {
        const sourceId = parseInt(droppableSource.droppableId);
        const destId = parseInt(droppableDestination.droppableId);

        if(column.id !== sourceId && column.id !== destId) return column;
        if(column.id === sourceId) return { ...column, data: sourceClone };
        if(column.id === destId) return { ...column, data: destClone };
        return column;
      });
    }
    case ACTIONS.MOVE.SUCCESS: {
      const lead = get(action, 'payload.data.data');
      // Ensure column_id is parsed as integer for comparison
      var leadColumnId = parseInt(lead.column_id);
      return currentState.map((column) => {
        if(column.id === leadColumnId){
          return {
            ...column,
            data: orderASC(column.data.map(l => l.id === lead.id ? parseAdditionalData(lead) : l))
          }
        }else{
          return column;
        }
      });
    }
    case ACTIONS.BULK_DELETE.SUCCESS: {
      const leadIds = get(action, 'meta.previousAction.payload.leadIds', []);
      return currentState.map((column) => ({
        ...column,
        data: column.data.filter(l => !leadIds.includes(l.id))
      }));
    }
    case ACTIONS.SET_SELECTED_MONTH: {
      const selectedMonth = action.payload.month;
      
      return currentState.map((column) => {
        if (column.id === COLUMN_IDS.POSITIVE || column.id === COLUMN_IDS.NEGATIVE) {
          const filteredColumn = currentState.filter(c => c.id === column.id);
          return {
            ...column,
            data: filteredColumn.map(c => c.data.filter(l => {
              const leadMonthYear = moment(l.column_updated_at).format('YYYY-MM');
              return leadMonthYear === selectedMonth;
            })),
            total: column.data.filter(l => {
              const leadMonthYear = moment(l.column_updated_at).format('YYYY-MM');
              return leadMonthYear === selectedMonth;
            }).length
          };
        }
        return column;
      });
    }
    default:
      return currentState;
  }
};

// Cadence summary reducer for landing page
const cadenceSummary = (state = { data: [], isLoading: false }, action) => {
  switch (action.type) {
    case ACTIONS.LOAD_CADENCE_SUMMARY.INIT:
      return {
        ...state,
        isLoading: true
      };
    case ACTIONS.LOAD_CADENCE_SUMMARY.SUCCESS:
      return {
        data: get(action, 'payload.data.data', []),
        isLoading: false
      };
    case ACTIONS.LOAD_CADENCE_SUMMARY.FAIL:
      return {
        ...state,
        isLoading: false
      };
    default:
      return state;
  }
};

// Cadence detailed summary reducer (for Summary component)
const cadenceDetailedSummary = (state = { data: null, isLoading: false, shouldRefresh: false }, action) => {
  switch (action.type) {
    case ACTIONS.LOAD_CADENCE_DETAILED_SUMMARY.INIT:
      return {
        ...state,
        isLoading: true,
        shouldRefresh: false
      };
    case ACTIONS.LOAD_CADENCE_DETAILED_SUMMARY.SUCCESS:
      return {
        ...state,
        data: get(action, 'payload.data.data', null),
        isLoading: false,
        shouldRefresh: false
      };
    case ACTIONS.LOAD_CADENCE_DETAILED_SUMMARY.FAIL:
      return {
        ...state,
        isLoading: false,
        shouldRefresh: false
      };
    // Invalidate summary when leads change
    case ACTIONS.CREATE.SUCCESS:
    case ACTIONS.BATCH.SUCCESS:
    case ACTIONS.SAVE.SUCCESS:
    case ACTIONS.DELETE.SUCCESS:
    case ACTIONS.MOVE.SUCCESS:
      return {
        ...state,
        shouldRefresh: true
      };
    default:
      return state;
  }
};

// Summary drawer reducer
const summaryDrawer = (state = { summaryData: null, leads: [], show: false, isLoading: false }, action) => {
  switch (action.type) {
    case 'SHOW_SUMMARY_DRAWER':
      return {
        summaryData: action.payload.summaryData,
        leads: [],
        show: true,
        isLoading: true
      };
    case ACTIONS.LOAD_SUMMARY_CARD_LEADS.INIT:
      return {
        ...state,
        isLoading: true
      };
    case ACTIONS.LOAD_SUMMARY_CARD_LEADS.SUCCESS:
      return {
        ...state,
        leads: get(action, 'payload.data.data', []).map(parseAdditionalData),
        isLoading: false
      };
    case ACTIONS.LOAD_SUMMARY_CARD_LEADS.FAIL:
      return {
        ...state,
        leads: [],
        isLoading: false
      };
    case 'CLOSE_SUMMARY_DRAWER':
      return {
        summaryData: null,
        leads: [],
        show: false,
        isLoading: false
      };
    default:
      return state;
  }
};

// Combine all reducers
const socialSellingReducer = (state, action) => {
    return {
        columns: columns(state && state.columns, action),
        drawer: drawer(state && state.drawer, action),
        summaryDrawer: summaryDrawer(state && state.summaryDrawer, action),
        cadenceSummary: cadenceSummary(state && state.cadenceSummary, action),
        cadenceDetailedSummary: cadenceDetailedSummary(state && state.cadenceDetailedSummary, action),
        isLoading: isLoading(state && state.isLoading, action),
        selectedMonth: selectedMonth(state && state.selectedMonth, action),
    };
};

const reducer = (state, action) => {
    // Ensure we have a default state
    const defaultState = {
        columns: [
            { id: 1, name: 'Inicio', style: { background: '#f5f5f5', color: '#722ed1', fontSize: '20px', textAlign: 'center' }, data: [], type: 'base' },
            { id: 2, name: 'Leads', style: { background: '#f5f5f5', color: '#1890ff', fontSize: '20px', textAlign: 'center' }, data: [], type: 'base' },
            { id: 5, name: 'Positivo', style: { background: '#f5f5f5', color: '#52c41a', fontSize: '20px', textAlign: 'center' }, data: [], type: 'base' },
            { id: 6, name: 'Negativo', style: { background: '#f5f5f5', color: '#f5222d', fontSize: '20px', textAlign: 'center' }, data: [], type: 'base' }
        ],
        drawer: { data: { id: null }, show: false, saving: false },
        summaryDrawer: { summaryData: null, leads: [], show: false, isLoading: false },
        cadenceSummary: { data: [], isLoading: false },
        cadenceDetailedSummary: { data: null, isLoading: false, shouldRefresh: false },
        isLoading: true,
        selectedMonth: moment().format("YYYY-MM-DD")
    };

    const currentState = state || defaultState;

    if (action.type === CACHE_ACTIONS.socialSelling) {
        return action.payload;
    }

    const newState = socialSellingReducer(currentState, action);
    
    // Update cache for relevant actions
    const updateCache = [
        ACTIONS.LOAD.SUCCESS,
        ACTIONS.CREATE.SUCCESS,
        ACTIONS.SAVE.SUCCESS,
        ACTIONS.DELETE.SUCCESS,
        ACTIONS.MOVE.SUCCESS,
        ACTIONS.SHOW,
        ACTIONS.CLOSE,
        'SHOW_SUMMARY_DRAWER',
        'CLOSE_SUMMARY_DRAWER'
    ].includes(action.type);

    if (updateCache) {
        LocalCacheHandler.save({
            pathname: CACHE_ACTIONS.socialSelling,
            data: newState
        });
    }

    return newState;
};

export default reducer;